import { Card, useMediaQuery, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useEffect, useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import Suspense from "@/components/molecules/Suspense";
import { useNotification } from "@/contexts/notification-context";
import { uniqueMessagesById } from "@/helpers/date-helpers";
import { useChatAttendanceActions } from "@/hooks/chat/use-chat-attendance-actions";
import useLoading from "@/hooks/use-loading";
import { IChat } from "@/interfaces/chat.interfaces";
import IUser from "@/interfaces/user.interface";
import { CurrentChatMessages } from "@/messages/chat/current-chat.messages";
import conversationService from "@/services/conversation.service";

import LoadingContainer from "../ChatLoading";
import CurrentChatContent from "../CurrentChatContent";
import CurrentChatHeader from "../CurrentChatHeader";
import CurrentChatInput from "../CurrentChatInput";
import { FilePreview } from "../FilePreview";

type AttendanceActions = ReturnType<typeof useChatAttendanceActions>;

type Props = {
  messages: CurrentChatMessages;
  isLoadingCurrentChat: boolean;
  user?: IUser | null;
  chats: IChat[] | null;
  isInitialLoad: boolean;
  setIsInitialLoad: (state: boolean) => void;
  onBack?: () => void;
  attendanceActions: AttendanceActions;
};

function CurrentChat({
  messages,
  isLoadingCurrentChat,
  user,
  chats,
  onBack,
  attendanceActions,
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const notification = useNotification();
  const { selectedChat, setSelectedChat, markAllMessageAsRead, activeChatPlatformId } = notification;
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [lastPageLoadTime, setLastPageLoadTime] = useState<Date | null>(null);
  const platformId = activeChatPlatformId;
  const {
    chatInputEnabled,
    canAssume,
    canFinish,
    assumeControl,
    finishControl,
    transferSteps,
    transferToStep,
  } = attendanceActions;

  const { startLoading, endLoading, isLoading: isLoadingMoreMessages } = useLoading();

  useEffect(() => {
    if (!selectedChat || !platformId) return;

    const hasAnyMessagesNotRead = selectedChat?.messages.items.some((message) => !message.readDate);

    if (!hasAnyMessagesNotRead) return;

    markAllMessageAsRead({ platformId, conversationId: selectedChat.conversationId });
  }, [markAllMessageAsRead, selectedChat, platformId]);

  const loadNextPageOfMessages = async () => {
    if (!selectedChat || !platformId) return;
    startLoading();
    setLastPageLoadTime(new Date());

    const pageNumber = selectedChat.messages.pageNumber! + 1;
    const pageSize = selectedChat.messages.pageSize;
    const contactId = selectedChat.contactId!;

    const response = await conversationService.getConversationDetails(
      { contactId, platformId },
      { pageNumber, pageSize }
    );

    setSelectedChat((prev) => {
      if (!prev) return null;

      return {
        ...prev,
        messages: {
          ...response,
          items: uniqueMessagesById([...response.items, ...prev.messages.items]),
        },
      };
    });

    endLoading();
  };

  const currentChatMessages = selectedChat?.messages.items || [];
  const totalMessages = selectedChat?.messages.totalCount || 0;
  const loadedMessages = selectedChat?.messages.items.length || 0;

  const shouldLoadChatHeader = !!selectedChat && !isLoadingCurrentChat && !!user;
  const shouldLoadChatInput = !!selectedChat && !isLoadingCurrentChat && !!user;

  return (
    <>
      {selectedChat || (chats && chats.length > 0) ? (
        <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Suspense isSuspended={isLoadingCurrentChat} fallback={<LoadingContainer />}>
            {shouldLoadChatHeader && (
              <CurrentChatHeader
                contactName={selectedChat.contactName}
                enableChatInput={assumeControl}
                disableChatInput={finishControl}
                chatInputEnabled={chatInputEnabled}
                canAssume={canAssume}
                canFinish={canFinish}
                onBack={onBack}
                transferSteps={transferSteps}
                onTransferToStep={transferToStep}
              />
            )}

            {loadedMessages < totalMessages ? (
              <MDBox display="flex" justifyContent="center" alignItems="center" mt={1}>
                <MDButton
                  isLoading={isLoadingMoreMessages}
                  variant="contained"
                  color="primary"
                  onClick={loadNextPageOfMessages}
                >
                  {messages.loadMoreMessages}
                </MDButton>
              </MDBox>
            ) : null}

            <MDBox sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
              <CurrentChatContent
                chatMessages={currentChatMessages}
                messages={messages}
                selectedChat={selectedChat}
                lastPageLoadTime={lastPageLoadTime}
              />
            </MDBox>

            {selectedFile && (
              <FilePreview
                file={selectedFile}
                onSelectFile={() => setSelectedFile(null)}
                messages={messages}
              />
            )}
            {shouldLoadChatInput && (
              <CurrentChatInput
                messages={messages}
                chat={selectedChat}
                user={user}
                onSelectFile={(file: File | null) => setSelectedFile(file)}
                selectedFile={selectedFile}
                chatInputEnabled={chatInputEnabled}
              />
            )}
          </Suspense>
        </Card>
      ) : (
        <Grid container justifyContent={"center"} sx={{ height: "100%" }}>
          <LoadingContainer showProgress={false} />
        </Grid>
      )}
    </>
  );
}

export default CurrentChat;
